/**
 * Centralized File Type Configuration
 * This file contains all supported file types and their configurations
 * Used across both frontend and backend for consistent validation
 */

export interface FileTypeConfig {
  mimeType: string;
  extensions: string[];
  displayName: string;
  maxSize: number; // in bytes
  category: 'image' | 'video';
  credits: number;
}

export interface FileTypesConfig {
  supportedTypes: FileTypeConfig[];
  allowedMimeTypes: string[];
  allowedExtensions: string[];
  imageTypes: string[];
  videoTypes: string[];
  maxImageSize: number;
  maxVideoSize: number;
}

// Supported file types configuration
const SUPPORTED_FILE_TYPES: FileTypeConfig[] = [
  // Image types
  {
    mimeType: 'image/jpeg',
    extensions: ['.jpg', '.jpeg'],
    displayName: 'JPEG',
    maxSize: 30 * 1024 * 1024, // 30MB - optimized for faster processing
    category: 'image',
    credits: 1
  },
  {
    mimeType: 'image/png',
    extensions: ['.png'],
    displayName: 'PNG',
    maxSize: 30 * 1024 * 1024, // 30MB - optimized for faster processing
    category: 'image',
    credits: 1
  },
  {
    mimeType: 'image/svg+xml',
    extensions: ['.svg'],
    displayName: 'SVG',
    maxSize: 30 * 1024 * 1024, // 30MB - optimized for faster processing
    category: 'image',
    credits: 1
  },
  {
    mimeType: 'application/postscript',
    extensions: ['.eps', '.ps'],
    displayName: 'EPS',
    maxSize: 30 * 1024 * 1024, // 30MB - optimized for faster processing
    category: 'image',
    credits: 2
  },
  // Video types
  {
    mimeType: 'video/mp4',
    extensions: ['.mp4'],
    displayName: 'MP4',
    maxSize: 500 * 1024 * 1024, // 500MB
    category: 'video',
    credits: 2
  },
  {
    mimeType: 'video/mov',
    extensions: ['.mov'],
    displayName: 'MOV',
    maxSize: 500 * 1024 * 1024, // 500MB
    category: 'video',
    credits: 2
  },
  {
    mimeType: 'video/quicktime',
    extensions: ['.mov'],
    displayName: 'QuickTime',
    maxSize: 500 * 1024 * 1024, // 500MB
    category: 'video',
    credits: 2
  }
];

// Generate derived configurations
const generateFileTypesConfig = (): FileTypesConfig => {
  const allowedMimeTypes = SUPPORTED_FILE_TYPES.map(type => type.mimeType);
  const allowedExtensions = SUPPORTED_FILE_TYPES.flatMap(type => type.extensions);
  const imageTypes = SUPPORTED_FILE_TYPES
    .filter(type => type.category === 'image')
    .map(type => type.mimeType);
  const videoTypes = SUPPORTED_FILE_TYPES
    .filter(type => type.category === 'video')
    .map(type => type.mimeType);
  
  const maxImageSize = Math.max(
    ...SUPPORTED_FILE_TYPES
      .filter(type => type.category === 'image')
      .map(type => type.maxSize)
  );
  
  const maxVideoSize = Math.max(
    ...SUPPORTED_FILE_TYPES
      .filter(type => type.category === 'video')
      .map(type => type.maxSize)
  );

  return {
    supportedTypes: SUPPORTED_FILE_TYPES,
    allowedMimeTypes,
    allowedExtensions,
    imageTypes,
    videoTypes,
    maxImageSize,
    maxVideoSize
  };
};

// Export the configuration
export const FILE_TYPES_CONFIG = generateFileTypesConfig();

// Helper functions
export const getFileTypeByMimeType = (mimeType: string): FileTypeConfig | undefined => {
  return SUPPORTED_FILE_TYPES.find(type => type.mimeType === mimeType);
};

export const getFileTypeByExtension = (extension: string): FileTypeConfig | undefined => {
  return SUPPORTED_FILE_TYPES.find(type => 
    type.extensions.includes(extension.toLowerCase())
  );
};

export const isImageType = (mimeType: string): boolean => {
  return FILE_TYPES_CONFIG.imageTypes.includes(mimeType);
};

export const isVideoType = (mimeType: string): boolean => {
  return FILE_TYPES_CONFIG.videoTypes.includes(mimeType);
};

export const isSupportedMimeType = (mimeType: string): boolean => {
  return FILE_TYPES_CONFIG.allowedMimeTypes.includes(mimeType);
};

export const isSupportedExtension = (extension: string): boolean => {
  return FILE_TYPES_CONFIG.allowedExtensions.includes(extension.toLowerCase());
};

export const getCreditsForMimeType = (mimeType: string): number => {
  const fileType = getFileTypeByMimeType(mimeType);
  return fileType?.credits || 0;
};

export const getMaxSizeForMimeType = (mimeType: string): number => {
  const fileType = getFileTypeByMimeType(mimeType);
  return fileType?.maxSize || 0;
};

// Display helpers
export const getSupportedFormatsDisplay = (): string => {
  return SUPPORTED_FILE_TYPES.map(type => type.displayName).join(', ');
};

export const getImageFormatsDisplay = (): string => {
  return SUPPORTED_FILE_TYPES
    .filter(type => type.category === 'image')
    .map(type => type.displayName)
    .join(', ');
};

export const getVideoFormatsDisplay = (): string => {
  return SUPPORTED_FILE_TYPES
    .filter(type => type.category === 'video')
    .map(type => type.displayName)
    .join(', ');
};